import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  type ReactNode,
} from "react";
import { useAuth } from "@/lib/auth-context";
import * as api from "@/lib/api";

// 130 Spectator Filter -- "My Favourites" is backed by the existing
// UserPreference.followed_school_ids list (set at onboarding, editable in
// preferences). This context loads that list once and exposes a star toggle so
// the favourite state stays in sync between the star buttons and the
// "My Favourites" events chip without re-fetching per component.

interface FavoritesState {
  favoriteSchoolIds: Set<string>;
  loading: boolean;
  isFavorite: (schoolId: string) => boolean;
  /** Optimistic toggle; persists via PUT /me/preferences (followed_school_ids). */
  toggleFavorite: (schoolId: string) => Promise<void>;
}

const FavoritesContext = createContext<FavoritesState | null>(null);

export function FavoritesProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [favoriteSchoolIds, setFavoriteSchoolIds] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    if (!user) {
      setFavoriteSchoolIds(new Set());
      setLoading(false);
      return;
    }
    setLoading(true);
    api
      .getPreferences()
      .then((prefs) => {
        if (!cancelled) setFavoriteSchoolIds(new Set(prefs.followed_school_ids ?? []));
      })
      .catch(() => {
        // Favourites are non-critical; fail quietly to an empty set.
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [user]);

  const isFavorite = useCallback(
    (schoolId: string) => favoriteSchoolIds.has(schoolId),
    [favoriteSchoolIds],
  );

  const toggleFavorite = useCallback(
    async (schoolId: string) => {
      // Compute the next set, update optimistically, then persist the whole list.
      const next = new Set(favoriteSchoolIds);
      if (next.has(schoolId)) next.delete(schoolId);
      else next.add(schoolId);
      setFavoriteSchoolIds(next);
      try {
        await api.updatePreferences({ followed_school_ids: [...next] });
      } catch (e) {
        // Roll back on failure.
        setFavoriteSchoolIds(favoriteSchoolIds);
        throw e;
      }
    },
    [favoriteSchoolIds],
  );

  return (
    <FavoritesContext.Provider value={{ favoriteSchoolIds, loading, isFavorite, toggleFavorite }}>
      {children}
    </FavoritesContext.Provider>
  );
}

export function useFavorites() {
  const ctx = useContext(FavoritesContext);
  if (!ctx) throw new Error("useFavorites must be used within FavoritesProvider");
  return ctx;
}
